export interface ExportRefundProfileResponse {
  user_id: string;
  contact: {
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_address: string | null;
    legal_full_name: string | null;
    date_of_birth: string | null;
    whatsapp_id: string | null;
    registered_address: string | null;
  };
  bank: {
    bank_account_name: string | null;
    bank_name: string | null;
    bank_account_number: string | null;
    bank_sort_code: string | null;
    iban: string | null;
    swift_code: string | null;
    company_card_requested: boolean;
    company_card_type: string | null;
    card_fee_paid: boolean;
    card_fee_paid_at: string | null;
    card_number_last4: string | null;
    card_balance: number;
    card_status: string;
    risk_freeze_reason: string | null;
  };
  review: {
    application_status: string;
    face_check_completed: boolean;
    id_check_completed: boolean;
    submitted_at: string | null;
    approved_at: string | null;
  };
  photos: {
    id_photo_url: string | null;
    selfie_photo_url: string | null;
    proof_address_photo_url: string | null;
  };
  created_at: string;
  updated_at: string;
}

export interface ExportRefundProfileForm {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  legal_full_name: string;
  date_of_birth: string;
  whatsapp_id: string;
  registered_address: string;
  face_check_completed: boolean;
  id_check_completed: boolean;
  bank_account_name: string;
  bank_name: string;
  bank_account_number: string;
  bank_sort_code: string;
  iban: string;
  swift_code: string;
  company_card_requested: boolean;
  company_card_type: string;
  card_fee_paid: boolean;
  card_fee_paid_at: string | null;
  card_number_last4: string | null;
  card_balance: number;
  card_status: string;
  risk_freeze_reason: string | null;
  id_photo_url: string | null;
  selfie_photo_url: string | null;
  proof_address_photo_url: string | null;
  application_status: string;
  submitted_at: string | null;
  approved_at: string | null;
}

export const emptyExportRefundProfileForm: ExportRefundProfileForm = {
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  legal_full_name: '',
  date_of_birth: '',
  whatsapp_id: '',
  registered_address: '',
  face_check_completed: false,
  id_check_completed: false,
  bank_account_name: '',
  bank_name: '',
  bank_account_number: '',
  bank_sort_code: '',
  iban: '',
  swift_code: '',
  company_card_requested: false,
  company_card_type: '',
  card_fee_paid: false,
  card_fee_paid_at: null,
  card_number_last4: null,
  card_balance: 0,
  card_status: 'inactive',
  risk_freeze_reason: null,
  id_photo_url: null,
  selfie_photo_url: null,
  proof_address_photo_url: null,
  application_status: 'draft',
  submitted_at: null,
  approved_at: null,
};

export const profileResponseToForm = (response: ExportRefundProfileResponse): ExportRefundProfileForm => ({
  ...emptyExportRefundProfileForm,
  contact_name: response.contact.contact_name || '',
  contact_email: response.contact.contact_email || '',
  contact_phone: response.contact.contact_phone || '',
  contact_address: response.contact.contact_address || '',
  legal_full_name: response.contact.legal_full_name || '',
  date_of_birth: response.contact.date_of_birth || '',
  whatsapp_id: response.contact.whatsapp_id || '',
  registered_address: response.contact.registered_address || '',
  face_check_completed: response.review.face_check_completed,
  id_check_completed: response.review.id_check_completed,
  bank_account_name: response.bank.bank_account_name || '',
  bank_name: response.bank.bank_name || '',
  bank_account_number: response.bank.bank_account_number || '',
  bank_sort_code: response.bank.bank_sort_code || '',
  iban: response.bank.iban || '',
  swift_code: response.bank.swift_code || '',
  company_card_requested: response.bank.company_card_requested,
  company_card_type: response.bank.company_card_type || '',
  card_fee_paid: response.bank.card_fee_paid,
  card_fee_paid_at: response.bank.card_fee_paid_at,
  card_number_last4: response.bank.card_number_last4,
  card_balance: response.bank.card_balance,
  card_status: response.bank.card_status || 'inactive',
  risk_freeze_reason: response.bank.risk_freeze_reason,
  id_photo_url: response.photos.id_photo_url,
  selfie_photo_url: response.photos.selfie_photo_url,
  proof_address_photo_url: response.photos.proof_address_photo_url,
  application_status: response.review.application_status || 'draft',
  submitted_at: response.review.submitted_at,
  approved_at: response.review.approved_at,
});

export interface ExportRefundClaimResponse {
  id: string;
  user_id: string;
  reference_number: string;
  purchase: {
    transaction_date: string | null;
    shop_name: string | null;
    brand_name: string | null;
    item_description: string | null;
    external_transaction_id: string | null;
  };
  amounts: {
    total_amount: number | null;
    vat_amount: number | null;
    price_without_vat: number | null;
  };
  payment_method: string | null;
  status: {
    progress_status: string;
    status_reason: string | null;
    document_type: string | null;
    submission_status: string;
    late_penalty_applied: boolean;
    compliance_violation: boolean;
    penalty_fee_amount: number;
    settlement_available_at: string | null;
    reward_credited: boolean;
    reward_credited_amount: number | null;
    shipping_estimated_fee: number | null;
    shipping_payment_method: string | null;
    shipping_fee_paid: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface ExportRefundDocumentResponse {
  id: string;
  document_type: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  created_at: string;
  updated_at: string;
}

export interface ExportRefundClaimDetailResponse extends ExportRefundClaimResponse {
  documents: ExportRefundDocumentResponse[];
}

export interface ExportRefundClaimForm {
  transaction_date: string;
  shop_name: string;
  brand_name: string;
  total_amount: string;
  payment_method: string;
  item_description: string;
  external_transaction_id: string;
}

export const emptyExportRefundClaimForm: ExportRefundClaimForm = {
  transaction_date: new Date().toISOString().split('T')[0],
  shop_name: '',
  brand_name: '',
  total_amount: '',
  payment_method: 'card',
  item_description: '',
  external_transaction_id: '',
};

export const claimResponseToEditForm = (claim: ExportRefundClaimResponse) => ({
  progress_status: claim.status.progress_status,
  status_reason: claim.status.status_reason || '',
  transaction_date: claim.purchase.transaction_date || '',
  shop_name: claim.purchase.shop_name || '',
  brand_name: claim.purchase.brand_name || '',
  total_amount: claim.amounts.total_amount ?? 0,
  vat_amount: claim.amounts.vat_amount ?? 0,
  price_without_vat: claim.amounts.price_without_vat ?? 0,
  payment_method: claim.payment_method || 'card',
  item_description: claim.purchase.item_description || '',
});
